import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar } from 'recharts';
import { useApi } from '../hooks/useApi';
import { formatCurrency } from '../utils/constants';
import { Target } from 'lucide-react';

const CorpusProjection = () => {
  const { post, loading } = useApi();
  const [projections, setProjections] = useState(null);
  const { register, handleSubmit } = useForm({
    defaultValues: { monthlyAmount: 10000, durationYears: 20, expectedReturnRate: 12 },
  });

  const onSubmit = async (data) => {
    try {
      const response = await post('/sip/projection', {
        monthlyAmount: Number(data.monthlyAmount),
        durationYears: Number(data.durationYears),
        expectedReturnRate: Number(data.expectedReturnRate),
      });
      setProjections(response.projections);
    } catch {
      // handled
    }
  };

  const lastProjection = projections?.[projections.length - 1];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg text-sm">
          <p className="font-semibold text-gray-700">Year {label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} style={{ color: entry.color }}>
              {entry.name}: {formatCurrency(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Target className="h-8 w-8 text-primary-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Corpus Projection</h1>
          <p className="text-gray-500">Visualize your wealth growth over time</p>
        </div>
      </div>

      {/* Input Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="card mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
          <div>
            <label className="label">Monthly SIP (₹)</label>
            <input type="number" className="input-field" {...register('monthlyAmount')} />
          </div>
          <div>
            <label className="label">Duration (Years)</label>
            <input type="number" className="input-field" {...register('durationYears')} />
          </div>
          <div>
            <label className="label">Expected Return (%)</label>
            <input type="number" step="0.5" className="input-field" {...register('expectedReturnRate')} />
          </div>
          <button type="submit" className="btn-primary h-10" disabled={loading}>
            {loading ? 'Loading...' : 'Project'}
          </button>
        </div>
      </form>

      {projections && (
        <div className="space-y-6">
          {/* Summary Cards */}
          {lastProjection && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="card text-center">
                <p className="text-sm text-gray-500">Total Invested</p>
                <p className="text-2xl font-bold text-gray-800">{formatCurrency(lastProjection.invested)}</p>
              </div>
              <div className="card text-center">
                <p className="text-sm text-gray-500">Wealth Gained</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(lastProjection.gains)}</p>
              </div>
              <div className="card text-center bg-primary-50 border-primary-200">
                <p className="text-sm text-primary-600">Final Corpus</p>
                <p className="text-2xl font-bold text-primary-700">{formatCurrency(lastProjection.corpus)}</p>
              </div>
            </div>
          )}

          {/* Area Chart */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Growth Over Time</h3>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={projections}>
                  <defs>
                    <linearGradient id="corpusGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="investedGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis tickFormatter={(v) => formatCurrency(v)} width={80} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area type="monotone" dataKey="invested" name="Invested" stroke="#22c55e" fill="url(#investedGrad)" strokeWidth={2} />
                  <Area type="monotone" dataKey="corpus" name="Corpus" stroke="#3b82f6" fill="url(#corpusGrad)" strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Bar Chart - Year by Year Gains */}
          <div className="card">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Year-by-Year Gains</h3>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={projections}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis dataKey="year" />
                  <YAxis tickFormatter={(v) => formatCurrency(v)} width={80} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="invested" name="Invested" fill="#22c55e" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="gains" name="Gains" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Year-by-year table */}
          <div className="card overflow-x-auto">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Detailed Breakdown</h3>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-600">Year</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-600">Invested</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-600">Corpus</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-600">Gains</th>
                </tr>
              </thead>
              <tbody>
                {projections.map((p) => (
                  <tr key={p.year} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-2 px-3 font-medium">{p.year}</td>
                    <td className="py-2 px-3 text-right">{formatCurrency(p.invested)}</td>
                    <td className="py-2 px-3 text-right font-semibold text-primary-600">{formatCurrency(p.corpus)}</td>
                    <td className="py-2 px-3 text-right text-green-600">{formatCurrency(p.gains)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default CorpusProjection;
